import { useState, useEffect, useCallback } from 'react';
import { 
  getFCMToken, 
  setupForegroundMessageListener, 
  isNotificationSupported, 
  getNotificationPermission,
  initializeMessaging 
} from './messaging';

/**
 * Custom hook for managing FCM token
 * @returns {Object} Object containing token, loading state, and error
 */
export const useFCMToken = () => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchToken = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const fcmToken = await getFCMToken();
      setToken(fcmToken);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching FCM token:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  return { token, loading, error, refetch: fetchToken };
};

/**
 * Custom hook for handling foreground messages
 * @param {Function} onMessageReceived - Callback for handling received messages
 * @returns {Object} Object containing message listener state
 */
export const useForegroundMessages = (onMessageReceived) => {
  const [isListening, setIsListening] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    if (!isNotificationSupported()) {
      console.warn('Notifications not supported in this browser');
      return;
    }

    const unsubscribe = setupForegroundMessageListener((payload) => {
      setLastMessage(payload);
      
      if (onMessageReceived) {
        onMessageReceived(payload);
      }
    });

    setIsListening(true);

    return () => {
      unsubscribe();
      setIsListening(false);
    };
  }, [onMessageReceived]);

  return { isListening, lastMessage };
};

/**
 * Custom hook for notification permission management
 * @returns {Object} Object containing permission state and methods
 */
export const useNotificationPermission = () => {
  const [permission, setPermission] = useState(getNotificationPermission());
  const [isSupported, setIsSupported] = useState(isNotificationSupported());

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      throw new Error('Notifications not supported in this browser');
    }

    try {
      const newPermission = await Notification.requestPermission();
      setPermission(newPermission);
      return newPermission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  }, [isSupported]);

  const checkPermission = useCallback(() => {
    const currentPermission = getNotificationPermission();
    setPermission(currentPermission);
    return currentPermission;
  }, []);

  return {
    permission,
    isSupported,
    isGranted: permission === 'granted',
    isDenied: permission === 'denied',
    isDefault: permission === 'default',
    requestPermission,
    checkPermission
  };
};

/**
 * Custom hook for Firebase messaging initialization
 * @returns {Object} Object containing initialization state and methods
 */
export const useFirebaseMessaging = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState(null);

  const initialize = useCallback(async () => {
    try {
      setIsInitializing(true);
      setError(null);
      
      const success = await initializeMessaging();
      setIsInitialized(success);
      
      if (!success) {
        setError('Failed to initialize Firebase messaging');
      }
    } catch (err) {
      setError(err.message);
      setIsInitialized(false);
    } finally {
      setIsInitializing(false);
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return {
    isInitialized,
    isInitializing,
    error,
    reinitialize: initialize
  };
};

/**
 * Combined hook for complete Firebase messaging functionality
 * @param {Function} onMessageReceived - Callback for handling received messages
 * @returns {Object} Complete Firebase messaging state and methods
 */
export const useFirebaseNotifications = (onMessageReceived) => {
  const fcmToken = useFCMToken();
  const foregroundMessages = useForegroundMessages(onMessageReceived);
  const permission = useNotificationPermission();
  const messaging = useFirebaseMessaging();

  return {
    // Token management
    token: fcmToken.token,
    tokenLoading: fcmToken.loading,
    tokenError: fcmToken.error,
    refetchToken: fcmToken.refetch,
    
    // Message handling
    isListening: foregroundMessages.isListening,
    lastMessage: foregroundMessages.lastMessage,
    
    // Permission management
    permission: permission.permission,
    isSupported: permission.isSupported,
    isGranted: permission.isGranted,
    isDenied: permission.isDenied,
    isDefault: permission.isDefault,
    requestPermission: permission.requestPermission,
    checkPermission: permission.checkPermission,
    
    // Initialization
    isInitialized: messaging.isInitialized,
    isInitializing: messaging.isInitializing,
    initError: messaging.error,
    reinitialize: messaging.reinitialize
  };
};
